import { CashReconciliationSummary, ReportStatus } from '../Model/cashReconciliationXModel'
import { cashReconciliationBXService as finance } from './cashReconciliationBXService'
import { stationOperationsReader } from './stationOperationsRequests'

// Ordinary finance screens use their existing service directly. Only station operations
// use this facade to share short-lived responses and space out actual HTTP requests.
export const stationOperationsReportService = {
  list: (params: Parameters<typeof finance.list>[0]) => {
    const key = new URLSearchParams({ page: String(params.page), limit: String(params.limit) })
    if (params.status) key.set('status', params.status)
    if (params.reportDate) key.set('reportDate', params.reportDate)
    return stationOperationsReader.read(`reports?${key}`, () => finance.list(params))
  },

  listAll: async (params: { status?: ReportStatus; reportDate?: string }) => {
    // Fetch larger batches independently of the UI's selected rows per page.
    const limit = 50
    const first = await stationOperationsReportService.list({ ...params, page: 1, limit })
    const rows = [...(first.data.data ?? [])]
    const total = first.data.meta?.total ?? rows.length
    const pageSize = first.data.meta?.limit ?? limit
    const pages = Math.max(1, Math.ceil(total / pageSize))
    for (let page = 2; page <= pages; page++) {
      const response = await stationOperationsReportService.list({ ...params, page, limit })
      rows.push(...(response.data.data ?? []))
    }
    return rows
  },

  listAllFinanceStatuses: async (
    statuses: ReportStatus[] = ['ASSOCIATE_APPROVED', 'FINANCE_CHANGES_REQUESTED', 'PUBLISHED'],
  ) => {
    const reports = new Map<string, CashReconciliationSummary>()
    for (const status of statuses) {
      const rows = await stationOperationsReportService.listAll({ status })
      rows.forEach((report) => reports.set(report.resourceId, report))
    }
    return Array.from(reports.values()).sort(
      (left, right) =>
        right.reportDate.localeCompare(left.reportDate) ||
        right.createdAt.localeCompare(left.createdAt),
    )
  },

  getById: (id: string) => stationOperationsReader.read(`report:${id}`, () => finance.getById(id)),
  getContents: (id: string) =>
    stationOperationsReader.read(`contents:${id}`, () => finance.getContents(id)),
  getAnalysis: (id: string) =>
    stationOperationsReader.read(`analysis:${id}`, () => finance.getAnalysis(id)),
  getDeliveries: (id: string) =>
    stationOperationsReader.read(`deliveries:${id}`, () => finance.getDeliveries(id)),
  getCashSalesReconciliation: (id: string) =>
    stationOperationsReader.read(`cash-reconciliation:${id}`, () =>
      finance.getCashSalesReconciliation(id),
    ),
}
